import { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import {
  EventsProfitReport,
  ProfitLossReport,
  ShopifyOverview,
  fetchEventProfitability,
  fetchProfitLoss,
  fetchSalesTax,
  fetchShopifyOverview,
} from "@/api/reports";
import type { SalesTaxReport } from "@/api/reports";
import Loading from "@/components/Loading";
import MainChart from "@/components/charts/MainChart";
import { EmptyPanel, SectionError, SkeletonRows } from "@/components/ui/error-ui";
type ChartsLibrary = typeof import("recharts");

const currency = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });

const TABS = [
  { id: "pnl", label: "Profit & Loss" },
  { id: "salesTax", label: "Sales Tax" },
  { id: "events", label: "Events" },
  { id: "shopify", label: "Shopify" },
];

type ReportsPanelProps = {
  standalone?: boolean;
};

export function ReportsPanel({ standalone = false }: ReportsPanelProps) {
  const [activeTab, setActiveTab] = useState<string>("pnl");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [groupBy, setGroupBy] = useState("state");
  const [stream, setStream] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pnl, setPnl] = useState<ProfitLossReport | null>(null);
  const [salesTax, setSalesTax] = useState<SalesTaxReport[]>([]);
  const [eventReports, setEventReports] = useState<EventsProfitReport[]>([]);
  const [shopify, setShopify] = useState<ShopifyOverview | null>(null);

  const filters = useMemo(
    () => ({ start: start || undefined, end: end || undefined, stream: stream || undefined }),
    [start, end, stream],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (activeTab === "pnl") {
        const data = await fetchProfitLoss(filters);
        setPnl(data);
      } else if (activeTab === "salesTax") {
        const data = await fetchSalesTax({ ...filters, group_by: groupBy });
        setSalesTax(data ?? []);
      } else if (activeTab === "events") {
        const data = await fetchEventProfitability(filters);
        setEventReports(data ?? []);
      } else if (activeTab === "shopify") {
        const data = await fetchShopifyOverview(filters);
        setShopify(data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load report");
    } finally {
      setLoading(false);
    }
  }, [activeTab, filters, groupBy]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className={standalone ? "space-y-6 p-6" : "space-y-6"}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-primary-foreground dark:text-amber-100">Financial reports</h1>
          <p className="text-sm text-muted-foreground">High-level summaries across ledgers, tax, events, and Shopify.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {TABS.map((tab) => (
            <Button
              key={tab.id}
              variant={tab.id === activeTab ? "default" : "secondary"}
              size="sm"
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </Button>
          ))}
        </div>
      </div>
      <div className="grid gap-4 md:grid-cols-4">
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          <span>Start date</span>
          <input
            type="date"
            value={start}
            onChange={(event) => setStart(event.target.value)}
            className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          <span>End date</span>
          <input
            type="date"
            value={end}
            onChange={(event) => setEnd(event.target.value)}
            className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
          />
        </label>
        {activeTab === "salesTax" && (
          <label className="flex flex-col gap-1 text-xs text-muted-foreground">
            <span>Group by</span>
            <select
              value={groupBy}
              onChange={(event) => setGroupBy(event.target.value)}
              className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
            >
              <option value="state">State</option>
              <option value="country">Country</option>
              <option value="city">City</option>
            </select>
          </label>
        )}
        {activeTab === "pnl" && (
          <label className="flex flex-col gap-1 text-xs text-muted-foreground">
            <span>Stream (source)</span>
            <input
              type="text"
              value={stream}
              onChange={(event) => setStream(event.target.value)}
              placeholder="e.g. bank_import"
              className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
            />
          </label>
        )}
      </div>
      {error && <SectionError message={error} onRetry={() => void load()} />}
      {loading && !error && <SkeletonRows rows={4} />}
      {!loading && !error && activeTab === "pnl" && (pnl ? <ProfitLossPanel data={pnl} /> : <EmptyPanel title="No P&L data for this period" description="Post journal entries or adjust the date range to see results." />)}
      {!loading && !error && activeTab === "salesTax" && <SalesTaxPanel rows={salesTax} />}
      {!loading && !error && activeTab === "events" && <EventsPanel rows={eventReports} />}
      {!loading && !error && activeTab === "shopify" && (shopify ? <ShopifyPanel overview={shopify} /> : <EmptyPanel title="No Shopify data" description="Connect your Shopify store in settings to see revenue here." />)}
    </div>
  );
}

function ProfitLossPanel({ data }: { data: ProfitLossReport }) {
  const trend = (data as any).trend ?? [];
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className="space-y-4">
        <InfoCard title="Income" subtitle="Total posted income" value={currency.format(Number(data.income))} />
        <InfoCard title="Expenses" subtitle="Total posted expense" value={currency.format(Number(data.expense))} />
        <InfoCard title="Net profit" subtitle="Income – Expenses" value={currency.format(Number(data.net_profit))} />
      </div>
      <div className="rounded-2xl border border-border bg-background/70 p-4">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">Monthly trend</p>
        {trend.length === 0 ? (
          <EmptyState message="No ledger activity for the selected period" />
        ) : (
          <ChartsSuspense label="Loading trend…">
            {({ ResponsiveContainer, LineChart, CartesianGrid, XAxis, YAxis, Tooltip, Legend, Line }) => (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={trend} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                  <XAxis dataKey="period" stroke="#94a3b8" tick={{ fontSize: 12 }} />
                  <YAxis
                    stroke="#94a3b8"
                    tickFormatter={(value) => currency.format(Number(value)).replace("$", "")}
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip formatter={(value) => currency.format(Number(value))} />
                  <Legend />
                  <Line type="monotone" dataKey="income" stroke="#facc15" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="expense" stroke="#ef4444" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </ChartsSuspense>
        )}
      </div>
    </div>
  );
}

function SalesTaxPanel({ rows }: { rows: SalesTaxReport[] }) {
  return (
    <div className="rounded-2xl border border-border bg-background/70 p-5">
      <h2 className="mb-3 text-lg font-semibold text-primary-foreground dark:text-amber-200">Sales tax liability</h2>
      {rows.length === 0 ? (
        <EmptyState message="No tax activity for this range" />
      ) : (
        <ChartsSuspense label="Loading tax chart…">
          {({ ResponsiveContainer, BarChart, CartesianGrid, XAxis, YAxis, Tooltip, Bar }) => (
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={rows} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                <XAxis dataKey="group" stroke="#94a3b8" tick={{ fontSize: 12 }} />
                <YAxis stroke="#94a3b8" tickFormatter={(value) => currency.format(Number(value)).replace("$", "")} />
                <Tooltip formatter={(value) => currency.format(Number(value))} />
                <Bar dataKey="tax_owed" fill="#f97316" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartsSuspense>
      )}
    </div>
  );
}

function EventsPanel({ rows }: { rows: EventsProfitReport[] }) {
  return (
    <div className="rounded-2xl border border-border bg-background/70 p-5">
      <h2 className="mb-3 text-lg font-semibold text-primary-foreground dark:text-amber-200">Event profitability</h2>
      {rows.length === 0 ? (
        <EmptyState message="No event data for this range" />
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {rows.map((row) => (
            <article key={row.event_id} className="rounded-xl border border-border/70 bg-background/60 p-4">
              <p className="text-sm font-semibold text-foreground">{row.event_name}</p>
              <p className="text-xs text-muted-foreground">{row.date}</p>
              <p className="mt-2 text-sm text-muted-foreground">Gross: {currency.format(Number(row.gross || 0))}</p>
              <p className="text-sm text-muted-foreground">Net: {currency.format(Number(row.net || 0))}</p>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

function ShopifyPanel({ overview }: { overview: ShopifyOverview }) {
  const entries = [
    { label: "Total orders", value: overview.total_orders },
    { label: "Gross sales", value: currency.format(Number(overview.gross_sales)) },
    { label: "Net sales", value: currency.format(Number(overview.net_sales)) },
  ];
  return (
    <div className="rounded-2xl border border-border bg-background/70 p-5">
      <h2 className="mb-3 text-lg font-semibold text-primary-foreground dark:text-amber-200">Shopify overview</h2>
      <div className="space-y-2">
        {entries.map((entry) => (
          <div key={entry.label} className="flex items-center justify-between text-sm text-foreground/80">
            <span>{entry.label}</span>
            <span className="font-semibold text-foreground">{entry.value}</span>
          </div>
        ))}
      </div>
      {overview.by_product?.length ? (
        <ChartsSuspense label="Loading Shopify chart…">
          {({ ResponsiveContainer, PieChart, Pie, Cell, Tooltip }) => (
            <ResponsiveContainer width="100%" height={280} className="mt-4">
              <PieChart>
                <Pie data={overview.by_product} dataKey="sales" nameKey="product" innerRadius={60} outerRadius={110} fill="#facc15" label>
                  {overview.by_product.map((entry, index) => (
                    <Cell key={`cell-${entry.product}-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => currency.format(Number(value))} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartsSuspense>
      ) : (
        <EmptyState message="No Shopify product breakdown available" />
      )}
    </div>
  );
}

const PIE_COLORS = ["#facc15", "#f97316", "#10b981", "#38bdf8", "#e879f9", "#f43f5e"];

function InfoCard({ title, subtitle, value }: { title: string; subtitle: string; value: string }) {
  return (
    <article className="rounded-xl border border-border bg-background/70 p-4 text-foreground/80">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{title}</p>
      <p className="text-2xl font-semibold text-foreground">{value}</p>
      <p className="text-xs text-muted-foreground">{subtitle}</p>
    </article>
  );
}

function EmptyState({ message }: { message: string }) {
  return <p className="text-sm text-muted-foreground">{message}</p>;
}

function ChartsSuspense({
  children,
  label = "Loading chart…",
}: {
  children: (charts: ChartsLibrary) => ReactNode;
  label?: string;
}) {
  return (
    <MainChart fallback={<Loading label={label} />} errorFallback={<Loading label={label} />}>
      {children}
    </MainChart>
  );
}
